import { getGoogleDriveFolderId } from './urlParser'
import { isItemAFolder } from './utility'
import Configs from 'infrastructure/config/environment'
import {
  GDriveFile,
  GDriveFileInfo,
  GDriveFileList,
  GDriveFileWithPath,
  GDriveSpreadsheetT,
  MimeTypes,
  SheetTabInput,
  SpreadsheetCreateRequest,
  WrapStrategy,
} from './types'
import { batchRequest } from './batch'
import { FileData, FolderData } from './fileSystem/types'
import { fetchWithExponentialBackoff } from 'infrastructure/api'
import type { GoogleUserAtomValue } from './auth'

const url = 'https://www.googleapis.com/drive/v3/files'
const sheetsUrl = 'https://content-sheets.googleapis.com/v4/spreadsheets'
const docxExportURL =
  'https://docs.google.com/feeds/download/documents/export/Export'

export function headers(token: string) {
  return {
    Authorization: `Bearer ${token}`,
  }
}

function getExportUrl(fileId: string, params: Record<string, string> = {}) {
  const searchParams = new URLSearchParams({
    ...params,
    key: Configs().GoogleApiKey,
  })
  return `${url}/${fileId}/export?${searchParams}`
}

function getExportNonDriveUrl(
  fileId: string,
  _params: Record<string, string> = {},
) {
  return `${docxExportURL}?id=${fileId}&exportFormat=html`
}

export async function getAllFiles(token: string, folderUrl: string) {
  let pageToken: string = ''
  const files: {
    id: string
    kind: 'drive#file'
    mimeType: string
    name: string
  }[] = []
  let errorResponse: { error: string } | undefined = undefined
  do {
    const data = await getFilesJsonResponse(token, folderUrl, { pageToken })
    if (data.error) {
      errorResponse = data
    }
    pageToken = data.nextPageToken
    files.push(...(data.files ?? []))
  } while (pageToken && !errorResponse)
  return errorResponse ?? files
}

export async function getFilesJsonResponse(
  token: string,
  folderURL: string,
  params: { pageToken?: string } = {},
) {
  const response = await getFiles(token, folderURL, params)
  return JSON.parse(response)
}

export async function getFileInfo(
  token: string,
  folderURL: string,
): Promise<
  | GDriveFileInfo
  | {
      error: {
        code: number
        errors: unknown[]
        message: string
        status: string
      }
    }
> {
  const folderID = getGoogleDriveFolderId(folderURL)
  const gdriveUrl =
    url +
    '/' +
    folderID +
    '?' +
    new URLSearchParams({
      key: Configs().GoogleApiKey,
      supportsAllDrives: String(true),
      fields: 'id,name,mimeType',
    })
  const response = await fetch(gdriveUrl, { headers: headers(token) })
  if (!response.ok) {
    return {
      error: {
        code: response.status,
        errors: [{ message: response.statusText }],
        message: response.statusText,
        status: 'ERROR',
      },
    }
  }
  return response.json()
}

export async function getFiles(
  token: string,
  folderURL: string,
  params: { pageToken?: string } = {},
) {
  const folderID = getGoogleDriveFolderId(folderURL)
  return fetchWithExponentialBackoff(
    url +
      '?' +
      new URLSearchParams({
        q: `'${folderID}' in parents`,
        key: Configs().GoogleApiKey,
        supportsAllDrives: String(true),
        includeItemsFromAllDrives: String(true),
        ...params,
      }),
    {
      headers: headers(token),
    },
    () => true,
    10,
    20_000,
    1.1, // increase delay slowly
  )
}

export async function getFileRequest(accessToken: string, fileId: string) {
  const folderID = await getGoogleDriveFolderId(fileId)
  return await fetch(
    `https://www.googleapis.com/drive/v2/files/${folderID}` +
      '?' +
      new URLSearchParams({
        key: Configs().GoogleApiKey,
        alt: 'media',
      }),
    {
      headers: headers(accessToken),
    },
  )
}

// returns all the files contained in a folder including subfolder files
// adds the name path to the files
// This is a flat list, does not maintain directory structure
export async function getFilesRecursive2(
  token: string,
  folderURL: string,
  currentNamePath?: string,
): Promise<GDriveFileWithPath[]> {
  const allFiles = await getAllFiles(token, folderURL)
  if (!Array.isArray(allFiles)) {
    return Promise.reject(new Error(JSON.stringify(allFiles)))
  }

  return allFiles.reduce<Promise<GDriveFileWithPath[]>>(async (acc, file) => {
    const nextPath = `${currentNamePath ?? ''}/${file.name}`
    const current = await acc
    if (isItemAFolder(file.mimeType)) {
      const result = await getFilesRecursive2(token, file.id, nextPath)
      current.push(...result)
    } else {
      current.push({
        ...file,
        namePath: nextPath,
      })
    }
    return current
  }, Promise.resolve([]))
}

export function isGDriveFileList(
  thing?: GDriveFileList | GDriveFile,
): thing is GDriveFileList {
  return thing?.kind === 'drive#fileList'
}

async function getTextBasedType(
  token: string,
  fileId: string,
  mimeType: string,
  params: Record<string, string> = {},
) {
  const result = await fetch(getExportUrl(fileId, { ...params, mimeType }), {
    headers: headers(token),
  })
  return result.text()
}

async function getTextBasedTypeDocx(
  token: string,
  fileId: string,
  params: Record<string, string> = {},
) {
  const result = await fetch(getExportNonDriveUrl(fileId, { ...params }), {
    headers: headers(token),
  })
  return result.text()
}

export async function getDoc(token: string, fileId: string) {
  const driveApi = 'https://www.googleapis.com/drive/v3/files'

  // Step 1: Get file metadata
  const metaRes = await fetch(`${driveApi}/${fileId}?fields=mimeType`, {
    headers: { Authorization: `Bearer ${token}` },
  })

  if (!metaRes.ok) {
    throw new Error(`Failed to retrieve metadata: ${metaRes.statusText}`)
  }

  const { mimeType } = await metaRes.json()

  // Step 2: Fetch content based on file type
  let url: string
  let isExport = false

  if (mimeType === 'application/vnd.google-apps.document') {
    // Google Doc → use export endpoint with mimeType text/html
    url = `${driveApi}/${fileId}/export?mimeType=text/html`
    isExport = true
  } else {
    // Binary file (e.g., .docx) → use get endpoint with alt=media
    url = `${driveApi}/${fileId}?alt=media`
  }

  const contentRes = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  })

  if (!contentRes.ok) {
    throw new Error(
      `Failed to ${isExport ? 'export' : 'download'} file: ${contentRes.statusText}`,
    )
  }

  return await contentRes.text()
}

export async function getDocx(token: string, fileId: string) {
  return getTextBasedTypeDocx(token, fileId)
}

export async function getSheet(token: string, fileId: string) {
  return getTextBasedType(token, fileId, MimeTypes.csv)
}

export function getSpreadsheetResponse(token: string, fileId: string) {
  return fetch(
    sheetsUrl +
      `/${getGoogleDriveFolderId(fileId)}?includeGridData=true&key=${Configs().GoogleApiKey}`,
    {
      headers: headers(token),
    },
  )
}

export async function getSpreadsheet(
  token: string,
  fileId: string,
): Promise<GDriveSpreadsheetT> {
  const response = await getSpreadsheetResponse(token, fileId)
  return response.json()
}

export async function createSpreadsheet(
  token: string,
  title: string,
  tabs?: SheetTabInput[],
): Promise<string> {
  const url = 'https://content-sheets.googleapis.com/v4/spreadsheets'

  const spreadsheetMetadata: SpreadsheetCreateRequest = {
    properties: { title },
  }

  if (tabs?.length) {
    spreadsheetMetadata.sheets = tabs.map((tab) => ({
      properties: {
        sheetId: tab.sheetId,
        title: tab.title,
        gridProperties: tab.frozenRowCount
          ? { frozenRowCount: tab.frozenRowCount }
          : undefined,
      },
      data: tab.data?.length
        ? [
            {
              rowData: tab.data.map((row) => ({
                values: row.map((cell) => ({
                  userEnteredValue: { stringValue: cell },
                })),
              })),
            },
          ]
        : undefined,
    }))
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(spreadsheetMetadata),
  })

  const data = await response.json()
  if (!response.ok) {
    throw new Error(`Error creating spreadsheet: ${data.error.message}`)
  }

  return data.spreadsheetId
}

// Function to append rows to the spreadsheet
export async function appendRowsToSpreadsheet(
  token: string,
  spreadsheetId: string,
  rows: string[][],
): Promise<Response> {
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`

  const addRowsRequestBody = {
    appendCells: {
      sheetId: 0, // Assuming Sheet1 is the first sheet
      rows: rows.map((row) => ({
        values: row.map((cell) => ({
          userEnteredValue: { stringValue: cell },
        })),
      })),
      fields: 'userEnteredValue',
    },
  }

  const requestBody = {
    requests: [addRowsRequestBody],
  }

  return fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  })
}

// Function to clear all values from a spreadsheet using the values API
export async function clearSpreadsheetValues(
  token: string,
  spreadsheetId: string,
  sheetName: string = 'Sheet1',
): Promise<Response> {
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${sheetName}:clear`

  return fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  })
}

// Function to update/replace all rows in the spreadsheet using the values API
export async function updateRowsInSpreadsheet(
  token: string,
  spreadsheetId: string,
  rows: string[][],
  sheetName: string = 'Sheet1',
): Promise<Response> {
  // First clear all existing values
  await clearSpreadsheetValues(token, spreadsheetId, sheetName)

  // Calculate the end column (A=1, B=2, etc.)
  const numColumns = rows[0]?.length || 1
  const endColumn = String.fromCharCode(64 + numColumns)
  const range = `${sheetName}!A1:${endColumn}${rows.length}`

  // Then update with new values starting from A1
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}?valueInputOption=RAW`

  const requestBody = {
    values: rows,
  }

  return fetch(url, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  })
}

// Function to highlight specific columns in the spreadsheet
export async function highlightSpreadsheetColumns(
  token: string,
  spreadsheetId: string,
  columnsToHighlight: number[],
): Promise<Response> {
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`

  // Prepare highlight requests for each column in the columnsToHighlight list
  const highlightRequests = columnsToHighlight.map((column) => {
    return {
      repeatCell: {
        range: {
          sheetId: 0,
          startColumnIndex: column,
          endColumnIndex: column + 1, // One past the column
          startRowIndex: 0,
        },
        cell: {
          userEnteredFormat: {
            backgroundColor: { red: 1, green: 1, blue: 0 }, // Yellow
          },
        },
        fields: 'userEnteredFormat.backgroundColor',
      },
    }
  })

  const requestBody = {
    requests: highlightRequests,
  }

  return fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  })
}

export async function setSpreadsheetWrapStrategy(
  token: string,
  spreadsheetId: string,
  wrapStrategy: WrapStrategy,
): Promise<Response> {
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`

  // Request to set the wrap strategy to "CLIP" for all cells in the sheet
  const requestBody = {
    requests: [
      {
        repeatCell: {
          range: {
            sheetId: 0,
            startRowIndex: 0, // Starting from the first row
            startColumnIndex: 0, // Starting from the first column
          },
          cell: {
            userEnteredFormat: {
              wrapStrategy: wrapStrategy,
            },
          },
          fields: 'userEnteredFormat.wrapStrategy',
        },
      },
    ],
  }

  return fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  })
}

// Function to find spreadsheets by name
export async function findSpreadsheetByName(
  token: string,
  name: string,
): Promise<string[]> {
  const url = 'https://www.googleapis.com/drive/v3/files'
  // Escape single quotes in the name for the query
  const escapedName = name.replace(/'/g, "\\'")
  const params = new URLSearchParams({
    q: `name='${escapedName}' and mimeType='application/vnd.google-apps.spreadsheet' and trashed=false`,
    fields: 'files(id)',
  })

  const response = await fetch(`${url}?${params}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  const data = await response.json()

  if (!response.ok) {
    const errorMessage =
      data.error?.message || response.statusText || 'Unknown error'
    throw new Error(`Error searching for spreadsheet: ${errorMessage}`)
  }

  return data.files?.map((file: { id: string }) => file.id) || []
}

// Create a folder and return its ID
export async function createFolder(
  token: string,
  folderName: string,
  parentId: string | null = null,
): Promise<string> {
  const url = 'https://www.googleapis.com/drive/v3/files'
  const folderMetadata = {
    name: folderName,
    mimeType: 'application/vnd.google-apps.folder',
    parents: parentId ? [parentId] : [],
  }

  try {
    const response = await fetchWithExponentialBackoff(
      url,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(folderMetadata),
      },
      () => true,
      10,
      20_000,
      1.1, // increase delay slowly
    )

    const data = JSON.parse(response)
    return data.id // Return the folder ID
  } catch (error) {
    throw new Error(`Error creating folder: ${error}`)
  }
}

function createBatchBodyForFiles(files: FileData[], boundary: string): string {
  let batchBody = ''

  files.forEach((f, index) => {
    const fileMetadata = {
      name: f.name,
      mimeType: f.mimeType,
      parents: [f.parentId],
    }

    batchBody += `--${boundary}\r\n`
    batchBody += `Content-Type: application/http\r\n`
    batchBody += `Content-Transfer-Encoding: binary\r\n\r\n`
    batchBody += `POST /drive/v3/files HTTP/1.1\r\n`
    batchBody += `Content-Type: application/json; charset=UTF-8\r\n`
    batchBody += `Content-ID: <${index}>\r\n\r\n`

    batchBody += `${JSON.stringify(fileMetadata)}\r\n\r\n`
  })

  batchBody += `--${boundary}--\r\n`
  return batchBody
}

export async function batchCreateFiles(
  token: string,
  files: FileData[],
): Promise<string> {
  const boundary = 'batch_boundary'
  const batchBody = createBatchBodyForFiles(files, boundary)
  return batchRequest(token, batchBody, boundary)
}

export async function updateFileContent(
  token: string,
  fileId: string,
  fileData: FileData,
): Promise<string> {
  // Prepare the content to be uploaded
  const body = new Blob([fileData.content], { type: fileData.mimeType })

  // Make the request to upload content to the file
  return fetchWithExponentialBackoff(
    `https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`,
    {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': fileData.mimeType,
      },
      body: body,
    },
    () => true,
    10,
    20_000,
    1.1, // increase delay slowly
  )
}

function createBatchBodyForFolders(
  folders: FolderData[],
  parentId: string,
  boundary: string,
): string {
  let batchBody = ''

  folders.forEach((f, index) => {
    const folderMetadata = {
      name: f.name,
      mimeType: 'application/vnd.google-apps.folder',
      parents: [parentId],
    }

    batchBody += `--${boundary}\r\n`
    batchBody += `Content-Type: application/http\r\n`
    batchBody += `Content-ID: <${index}>\r\n\r\n`
    batchBody += `POST /drive/v3/files HTTP/1.1\r\n`
    batchBody += `Content-Type: application/json\r\n\r\n`
    batchBody += `${JSON.stringify(folderMetadata)}\r\n`
  })

  batchBody += `--${boundary}--\r\n`
  return batchBody
}

export async function batchCreateFolders(
  token: string,
  folders: FolderData[],
  parentId: string,
): Promise<string> {
  const boundary = 'batch_boundary'
  const batchBody = createBatchBodyForFolders(folders, parentId, boundary)

  return batchRequest(token, batchBody, boundary)
}

export const AuthError = new Error('401 Error')

// This is useful when you want to easily convert csv data to a typed list
// but I think we can create something to do that with the result from
// the getSpreadsheet function
export async function getSheetTab({
  token,
  fileId,
  tabId,
}: {
  token: string
  fileId: string
  tabId?: string
}) {
  const theUrl = `https://docs.google.com/spreadsheets/export?id=${fileId}&exportFormat=csv${tabId ? `&gid=${tabId}` : ''}`
  const result = await fetch(theUrl, { headers: headers(token) })
  if (result.status === 401) {
    return {
      error: AuthError,
      result: undefined,
    }
  }
  return {
    error: undefined,
    result: await result.text(),
  }
}

export async function getDrivePdfFile(
  guser: GoogleUserAtomValue,
  fileId: string,
) {
  if (!guser.credential?.accessToken) return { error: 'No access token' }
  const accessToken = guser.credential.accessToken
  const response = await fetch(
    `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media&supportsAllDrives=true`,
    {
      headers: headers(accessToken),
    },
  )
  if (!response.ok) {
    let detail = ''
    try {
      detail = (await response.json()).error?.message || ''
    } catch {
      detail = ''
    }
    return { error: `HTTP ${response.status}${detail ? ' — ' + detail : ''}` }
  }
  const blob = await response.blob()
  // Ensure the type is preserved as PDF
  return {
    file: new Blob([await blob.arrayBuffer()], { type: 'application/pdf' }),
  }
}
